import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, unref as _unref, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "completion-recommendations" }
const _hoisted_2 = { class: "completion-recommendations__list" }
const _hoisted_3 = ["onClick"]
const _hoisted_4 = { class: "completion-recommendations__summary" }
const _hoisted_5 = { class: "completion-recommendations__badges" }
const _hoisted_6 = { class: "completion-recommendations__details" }
const _hoisted_7 = { class: "completion-recommendations__category" }
const _hoisted_8 = { class: "completion-recommendations__book-title" }
const _hoisted_9 = { class: "completion-recommendations__author" }
const _hoisted_10 = { class: "completion-recommendations__pitch" }
const _hoisted_11 = ["aria-label", "disabled", "onClick"]

import { ref } from 'vue'

import BookCardThumbnail from '@/app/components/book/card/BookCardThumbnail.vue'
import BookListenIcon from '@/app/components/book/card/BookListenIcon.vue'
import FreeBookIcon from '@/app/components/book/card/FreeBookIcon.vue'
import TimeEstimate from '@/app/components/doc/TimeEstimate.vue'
import type { CompletionRecommendationData } from '@/models/completion'
import { backend } from '@/services/backend'
import { vOnVisible } from '@/helpers/directives'
import {
  DOCS_USER_DATA_CACHE,
  HOME_CACHE,
  resetCacheByKeys,
} from '@/helpers/cache'


export default /*@__PURE__*/_defineComponent({
  __name: 'CompletionRecommendations',
  props: {
    recommendations: {}
  },
  emits: ["select", "favorite", "impression"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const favoriteIds = ref(
  new Set(
    props.recommendations
      .filter((recommendation) => recommendation.is_favorite)
      .map((recommendation) => recommendation.book.id),
  ),
)
const favoriteRequestId = ref<string | null>(null)

function isFavorite(bookId: string): boolean {
  return favoriteIds.value.has(bookId)
}

async function toggleFavorite(
  recommendation: CompletionRecommendationData,
  position: number,
): Promise<void> {
  if (favoriteRequestId.value !== null) {
    return
  }

  const book = recommendation.book
  favoriteRequestId.value = book.id
  emit(
    'favorite',
    recommendation,
    position,
    isFavorite(book.id)
      ? 'unfavorite-recommendation'
      : 'favorite-recommendation',
  )

  try {
    const favoriteData = {
      id: book.id,
      title: book.title,
      doc_type: 'book' as const,
      is_free: book.is_free,
      summary_words: book.summary_words,
    }
    const result = isFavorite(book.id)
      ? await backend.unfavorite(favoriteData)
      : await backend.favorite(favoriteData)

    const updatedFavoriteIds = new Set(favoriteIds.value)
    if (result.is_favorite) {
      updatedFavoriteIds.add(book.id)
    } else {
      updatedFavoriteIds.delete(book.id)
    }
    favoriteIds.value = updatedFavoriteIds

    // Other screens read favorite state from these caches, while this popup
    // keeps its own state. Clear them so those screens load the updated value.
    await resetCacheByKeys([HOME_CACHE, DOCS_USER_DATA_CACHE])
  } finally {
    favoriteRequestId.value = null
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _cache[0] || (_cache[0] = _createElementVNode("h2", { class: "completion-recommendations__title" }, "What's next?", -1)),
    _createElementVNode("div", _hoisted_2, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.recommendations, (recommendation, index) => {
        return _withDirectives((_openBlock(), _createElementBlock("article", {
          key: recommendation.book.id,
          class: "completion-recommendations__item"
        }, [
          _createElementVNode("button", {
            class: "completion-recommendations__card",
            type: "button",
            onClick: ($event: any) => (emit('select', recommendation, index + 1))
          }, [
            _createElementVNode("div", _hoisted_4, [
              _createVNode(BookCardThumbnail, {
                class: "completion-recommendations__thumbnail",
                doc: {
                title: recommendation.book.title,
                author: recommendation.book.author,
                cover_image: recommendation.book.cover_image ?? undefined,
              }
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_5, [
                    (recommendation.book.has_audio)
                      ? (_openBlock(), _createBlock(BookListenIcon, { key: 0 }))
                      : _createCommentVNode("", true),
                    (recommendation.book.is_free)
                      ? (_openBlock(), _createBlock(FreeBookIcon, { key: 1 }))
                      : _createCommentVNode("", true)
                  ])
                ]),
                _: 2
              }, 1032, ["doc"]),
              _createElementVNode("div", _hoisted_6, [
                _createElementVNode("p", _hoisted_7, _toDisplayString(recommendation.book.tags[0]), 1),
                _createElementVNode("h3", _hoisted_8, _toDisplayString(recommendation.book.title), 1),
                _createElementVNode("p", _hoisted_9, _toDisplayString(recommendation.book.author), 1),
                (recommendation.book.summary_words)
                  ? (_openBlock(), _createBlock(TimeEstimate, {
                      key: 0,
                      doc: recommendation.book
                    }, null, 8, ["doc"]))
                  : _createCommentVNode("", true)
              ])
            ]),
            _createElementVNode("p", _hoisted_10, _toDisplayString(recommendation.sales_pitch), 1)
          ], 8, _hoisted_3),
          _createElementVNode("button", {
            class: "completion-recommendations__favorite",
            type: "button",
            "aria-label": 
            isFavorite(recommendation.book.id)
              ? `Remove ${recommendation.book.title} from Favorites`
              : `Add ${recommendation.book.title} to Favorites`
          ,
            disabled: favoriteRequestId.value !== null,
            onClick: ($event: any) => (toggleFavorite(recommendation, index + 1))
          }, [
            _createElementVNode("span", {
              class: _normalizeClass(["iconfont", isFavorite(recommendation.book.id) ? 'iflag2' : 'iflag'])
            }, null, 2)
          ], 8, _hoisted_11)
        ])), [
          [
            _unref(vOnVisible),
            () => emit('impression', recommendation, index + 1),
            void 0,
            { once: true }
          ]
        ])
      }), 128))
    ])
  ]))
}
}

})